/**
 * AdBanner — Sidebar (desktop fixed) + JOB_DETAIL + FOOTER banneri.
 *
 * variant:
 *   "sidebar"  — 300×250, desktop-da fixed; mobile-da inline
 *   "detail"   — 300×250, elan detal sağ panelinin altı
 *   "footer"   — full-width, 728×90 / 970×90
 *   "mobile"   — 320×100 horizontal, həmişə inline
 *
 * Görünmə: yalnız reklam viewport-da ≥50% görünəndə (1 san.) sayılır.
 * Klik: hər klik backend-ə qeydə alınır.
 */
import { useEffect, useState, useRef } from 'react'
import api from '../api/axios'
import { useAuth } from '../context/AuthContext'
import { safeUrl } from '../utils/safeUrl'

function AdLink({ url, adId, children, style }) {
  const handleClick = () => {
    api.post(`/ads/${adId}/click`).catch(() => {})
  }

  const href = safeUrl(url)
  if (!href) {
    return <span style={style}>{children}</span>
  }
  if (href.startsWith('/')) {
    return (
      <a href={href} style={style} onClick={handleClick}>
        {children}
      </a>
    )
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
       style={style} onClick={handleClick}>
      {children}
    </a>
  )
}

function AdLabel() {
  return (
    <div style={{
      position: 'absolute', top: 8, right: 8, zIndex: 2,
      fontSize: '.58rem', fontWeight: 700, letterSpacing: '.1em',
      padding: '.15rem .45rem', borderRadius: '100px',
      background: 'rgba(0,0,0,.55)', backdropFilter: 'blur(4px)',
      border: '1px solid rgba(var(--accent-rgb),.2)', color: 'var(--accent)',
      textTransform: 'uppercase',
    }}>
      Reklam
    </div>
  )
}

export default function AdBanner({ placement, variant = 'sidebar', style: extraStyle = {} }) {
  const { user }        = useAuth()
  const [ad, setAd]     = useState(null)
  const ref             = useRef(null)
  const viewed          = useRef(false)
  const timerRef        = useRef(null)

  const fetchAd = () => {
    if (user?.vip) return
    api.get('/ads', { params: { placement } })
      .then(r => { setAd(r.data); viewed.current = false })
      .catch(() => setAd(null))
  }

  useEffect(() => {
    if (user?.vip) { setAd(null); return }
    fetchAd()
    const iv = setInterval(fetchAd, 60_000)
    return () => clearInterval(iv)
  }, [placement, user?.vip])

  // IntersectionObserver — yalnız görünəndə say
  useEffect(() => {
    if (!ad || !ref.current) return
    viewed.current = false

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          timerRef.current = setTimeout(() => {
            if (!viewed.current) {
              viewed.current = true
              api.post(`/ads/${ad.id}/view`).catch(() => {})
            }
          }, 1000)
        } else {
          clearTimeout(timerRef.current)
        }
      },
      { threshold: 0.5 }
    )

    observer.observe(ref.current)
    return () => {
      observer.disconnect()
      clearTimeout(timerRef.current)
    }
  }, [ad?.id])

  if (!ad) return null

  if (variant === 'footer') {
    return (
      <AdLink url={ad.targetUrl} adId={ad.id} style={{ textDecoration: 'none', display: 'block', ...extraStyle }}>
        <div ref={ref} style={{
          position: 'relative', borderRadius: 'var(--radius)', overflow: 'hidden',
          border: '1px solid rgba(var(--accent-rgb),.15)', background: 'rgba(var(--accent-rgb),.02)',
        }}>
          <AdLabel />
          <picture>
            <source media="(min-width: 1024px)" srcSet={ad.imageDesktop} />
            <source media="(min-width: 640px)" srcSet={ad.imageTablet} />
            <img loading="lazy"
              src={ad.imageMobile}
              alt={ad.title}
              style={{ width: '100%', height: 'auto', display: 'block', maxHeight: 100, objectFit: 'cover' }}
            />
          </picture>
        </div>
      </AdLink>
    )
  }

  // sidebar / detail / mobile — box
  return (
    <AdLink url={ad.targetUrl} adId={ad.id} style={{ textDecoration: 'none', display: 'block', ...extraStyle }}>
      <div
        ref={ref}
        style={{
          borderRadius: 'var(--radius)', overflow: 'hidden', position: 'relative',
          border: '1px solid rgba(var(--accent-rgb),.18)',
          background: 'rgba(var(--accent-rgb),.03)',
          transition: 'border-color .2s',
        }}
        onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(var(--accent-rgb),.4)'}
        onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(var(--accent-rgb),.18)'}
      >
        <AdLabel />
        <picture>
          <source media="(min-width: 640px)" srcSet={ad.imageDesktop} />
          <img loading="lazy" src={ad.imageMobile} alt={ad.title} style={{ width: '100%', height: 'auto', display: 'block' }} />
        </picture>
        <div style={{ padding: '.75rem 1rem' }}>
          <div style={{ fontWeight: 700, fontSize: '.82rem', marginBottom: '.2rem', color: 'var(--text)' }}>{ad.title}</div>
          <div style={{
            fontSize: '.72rem', color: 'var(--muted)', lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {ad.description}
          </div>
          <div style={{ marginTop: '.5rem' }}>
            <span style={{ fontSize: '.72rem', color: 'var(--muted)' }}>{ad.advertiserName}</span>
          </div>
        </div>
      </div>
    </AdLink>
  )
}
